import { Button } from "components/DesignSystem/Button/Button";
import { Price } from "components/Price";
import { ProductCard } from "components/ProductCard/ProductCard";
import {
  GroupSubscriptionState,
  Subscription,
  SubscriptionInProfile,
} from "types/Models/subscription";
import { RASubscribedState } from "./RASubscribedState";
import { SalesTaxFilingSubscribedState } from "./SalesTaxFilingSubscribedState";
import { SubscriptionTypeCard } from "./SubscriptionTypeCard";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useContext, useState } from "react";
import authContext from "jwt_context&axios/authContext";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import Lightning from "static/images/Lightning.svg";
import { TaxSoftwareSubscriptionsModal } from "components/TaxSoftwareSubscriptionsModal";
import { AddonData, setAddonData, setAddonType } from "store/slices/addons";
import { useDispatch } from "react-redux";
import { _MANAGE } from "constants/addons";
import { setSubscriptionSummaryModalOpen } from "store/slices/subscriptionSummary";
import { ProductGroup } from "store/apis/products";
import {
  BOOKKEEPING_V2,
  FRACTIONAL_PAYROLL_ADMINISTRATOR,
  MAILROOM,
  PLATFORM,
  BOOKS_PLUS_PLAN,
  REGISTERED_AGENT,
  SALES_TAX_FILING,
} from "constants/addons";
import { useQuery } from "hooks/useQuery";
import {
  PRODUCT_BOOKS,
  PRODUCT_TAX,
  PRODUCT_MAILROOM,
  PRODUCT_SALESTAX,
  PRODUCT_INTRAGROUP,
  FREE_PLANS,
} from "constants/billing";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { stateRegistrationsApis } from "store/apis/StateRegistrations";
import Loader from "components/DesignSystem/Loader/loader";
import { Cart } from "components/icons/Cart";

type ActiveSubscriptionContainerProps = {
  allSubscriptions: Subscription[];
  groupId: string;
  entityId: string;
  products?: Record<string, ProductGroup>;
};
export const ActiveSubscriptionContainer = ({
  allSubscriptions,
  groupId,
  entityId,
  products,
}: ActiveSubscriptionContainerProps) => {
  const { authtoken } = useContext(authContext);
  const dispatch = useDispatch();
  const {
    data: subscribedPlansInlcudingInactive,
    isLoading,
    isError,
    isSuccess,
  } = useGetSubscriptionsQuery(
    {
      groupId,
      entityId,
      subscription_types: PLATFORM,
      get_inactive: true,
    },
    { skip: !groupId || !entityId }
  );
  const { platform_subscription: groupPlatformSubscription } =
    useCurrentGroupContext();
  let {
    platform_subscription: currentSubscription,
  }: {
    platform_subscription: SubscriptionInProfile;
  } = authtoken || {};

  if (groupId) {
    currentSubscription = groupPlatformSubscription;
  }

  const depricatedTaxSubscription = subscribedPlansInlcudingInactive?.find(
    (item) => item.uuid === currentSubscription?.subscription_uuid
  );
  const query = useQuery();

  const isBooksActive = query.get(PRODUCT_BOOKS);
  const isTaxActive = query.get(PRODUCT_TAX);
  const isMailroomActive = query.get(PRODUCT_MAILROOM);
  const isSalesTaxActive = query.get(PRODUCT_SALESTAX);
  const isIntragroupActive = query.get(PRODUCT_INTRAGROUP);
  const search = query.get("search") || "";
  const [isAddToCartFlow, setAddToCartFlow] = useState(false);

  const isNoFilterSelected =
    !isBooksActive &&
    !isTaxActive &&
    !isMailroomActive &&
    !isSalesTaxActive &&
    !isIntragroupActive;

  const isFilterNotAppliedExceptTax =
    !isBooksActive &&
    !isMailroomActive &&
    !isSalesTaxActive &&
    !isIntragroupActive;

  const order = ["BOOKS", "TAX", "MAILROOM", "RND", "SALES_TAX", "INTRA_GROUP"];
  const tempCards: Subscription[] = [];

  (Object.keys(products ?? {}) || [])
    .sort((productTypeA, productTypeB) => {
      const indexA = order.indexOf(productTypeA);
      const indexB = order.indexOf(productTypeB);
      return (
        (indexA === -1 ? Infinity : indexA) -
        (indexB === -1 ? Infinity : indexB)
      );
    })
    .forEach((productType) => {
      const categories = products?.[productType] ?? {};
      Object.entries(categories).forEach(([categoryName, items]) => {
        if (!Array.isArray(items) || items.length === 0) return;
        const filteredItems = items.filter(
          (item): item is Subscription =>
            "subscription_name" in item &&
            Array.isArray(item.group_subscription) &&
            item.group_subscription.length > 0
        );
        tempCards.push(...filteredItems);
      });
    });

  let allSubscriptionsList: Subscription[] = tempCards
    ?.filter(
      (
        item: Subscription
      ): item is Subscription & {
        group_subscription: GroupSubscriptionState[];
      } => Array.isArray(item?.group_subscription)
    )
    ?.flatMap((item) =>
      item.group_subscription.length > 1
        ? item.group_subscription.map((group: GroupSubscriptionState) => ({
            ...item,
            group_subscription: [group],
          }))
        : [item]
    );
  const hasPlusPlan = allSubscriptionsList.some(
    (item) =>
      item.subscription_name === "Inkle Books (Plus Plan)" ||
      item.subscription_name === "Inkle Books Standard Plan"
  );
  const hasTaxPlusPlan = allSubscriptionsList.some(
    (item) => item.subscription_type === PLATFORM
  );

  if (
    depricatedTaxSubscription &&
    isFilterNotAppliedExceptTax &&
    !hasTaxPlusPlan
  ) {
    allSubscriptionsList = allSubscriptionsList.filter(
      (item) => item.subscription_type !== PLATFORM
    );
    allSubscriptionsList.push({
      ...depricatedTaxSubscription,
      group_subscription: depricatedTaxSubscription.group_subscription || [],
    });
  }

  const isBooksPlanIncludedInSearch = "inkle books free plan".includes(
    search.toLowerCase()
  );

  if (
    !hasPlusPlan &&
    (isNoFilterSelected || isBooksActive) &&
    (search ? isBooksPlanIncludedInSearch : true)
  ) {
    allSubscriptionsList.push({
      subscription_name: "Inkle Books Free Plan",
      amount: 0,
      billing_cycle: "MONTHLY",
      group_subscription: [],
      uuid: "",
      description: "",
      upgrade_level: 0,
      subscription_type: BOOKS_PLUS_PLAN,
      product_content_type_id: 0,
      plan_access_data: {},
      product_type: "BOOKS",
    });
  }

  const isDiscountApplied = allSubscriptionsList.some(
    (item) =>
      item.stripe_coupon?.discount_rate && item.stripe_coupon.discount_rate > 0
  );

  const taxSoftwareSubscriptions = allSubscriptions.find(
    (item) => item.subscription_type === PLATFORM
  );

  const booksSoftwareSubscriptionsModal = useModal();
  const taxSoftwareSubscriptionsModal = useModal();

  const handleUpgrade = (type: string) => {
    switch (type) {
      case BOOKS_PLUS_PLAN:
        booksSoftwareSubscriptionsModal.open();
        break;
      case PLATFORM:
        taxSoftwareSubscriptionsModal.open();
        break;
      default:
        break;
    }
  };

  const onManageHandler = (addon: AddonData, type: string) => {
    if (type === BOOKS_PLUS_PLAN || type === FRACTIONAL_PAYROLL_ADMINISTRATOR) {
      dispatch(setAddonData(addon));
      dispatch(setAddonType(addon?.subscription_type + _MANAGE));
    } else {
      dispatch(setSubscriptionSummaryModalOpen(true));
    }
  };

  const onProductSubscribe = () => {
    dispatch(
      stateRegistrationsApis.util.invalidateTags([
        "PLATFORM_SUBSCRIPTION_PRODUCT",
      ])
    );
  };

  const handleAddToCart = () => {
    setAddToCartFlow(true);
    taxSoftwareSubscriptionsModal.open();
  };

  return (
    <section className="t-flex t-gap-[0.9375rem] t-flex-wrap t-items-center t-pt-3.5">
      <Async.Root
        isEmpty={
          isSuccess && (!allSubscriptionsList || !allSubscriptionsList.length)
        }
        isError={isError}
        isSuccess={isSuccess}
        isLoading={isLoading}
        customLoader={
          <div className="t-w-full t-h-full t-flex t-justify-center t-items-center t-pt-40">
            <Loader size="small" />
          </div>
        }
      >
        <Async.Success>
          {allSubscriptionsList?.map((item, index) => {
            const showRASubscribedState =
              item.subscription_type === REGISTERED_AGENT;

            const showSalesTaxFilingSubscribedState =
              item.subscription_type === SALES_TAX_FILING;

            const showSubscriptionTypeCard =
              item.subscription_type === MAILROOM ||
              item.subscription_type === BOOKKEEPING_V2;

            return (
              <>
                {showRASubscribedState ? (
                  <RASubscribedState
                    key={index}
                    subscribedState={item.group_subscription?.at(0)!}
                    statePriceMap={item.ra_state_amount_maps}
                    otherAmount={item.amount}
                    isDiscountApplied={isDiscountApplied}
                  />
                ) : showSalesTaxFilingSubscribedState ? (
                  <SalesTaxFilingSubscribedState
                    key={index}
                    subscribedState={item.group_subscription?.at(0)!}
                    amount={item.amount}
                  />
                ) : showSubscriptionTypeCard ? (
                  <SubscriptionTypeCard
                    key={item.uuid}
                    subscription={item as Subscription}
                    isPrimarySubscription={item.subscription_type === MAILROOM}
                    isDiscountApplied={isDiscountApplied}
                  />
                ) : (
                  <ProductCard
                    key={index}
                    title={item.subscription_name}
                    isDiscountApplied={isDiscountApplied}
                    planStatus="ACTIVE"
                    isPrimarySubscription
                    price={
                      item.subscription_type ===
                      FRACTIONAL_PAYROLL_ADMINISTRATOR ? null : item.group_subscription!
                          .length > 0 ? (
                        <Price
                          amount={item.amount}
                          size="small"
                          cycle={item.billing_cycle}
                        />
                      ) : (
                        <div className="t-text-body-sm t-font-bold t-text-text-60">
                          Free
                        </div>
                      )
                    }
                    description={
                      <div className="t-text-body-sm t-text-white">
                        Take your accounting to the next level with premium
                        features and automation.
                      </div>
                    }
                    action={
                      FREE_PLANS.includes(item.subscription_name) ? (
                        <div className="t-flex t-items-center t-gap-1">
                          <Button
                            size="small"
                            onClick={() =>
                              handleUpgrade(item.subscription_type)
                            }
                            customType="primary_outlined"
                          >
                            <img src={Lightning} alt="Lightning" /> &nbsp;
                            Upgrade
                          </Button>
                          <Button
                            customType="primary_outlined"
                            size="small"
                            onClick={handleAddToCart}
                          >
                            <Cart />
                          </Button>
                          {!hasPlusPlan && (
                            <BooksSoftwareSubscriptionsModal
                              open={booksSoftwareSubscriptionsModal.isOpen}
                              onClose={() =>
                                booksSoftwareSubscriptionsModal.close()
                              }
                              entityId={entityId}
                              onProductSubscribe={onProductSubscribe}
                            />
                          )}
                          {!hasTaxPlusPlan && (
                            <TaxSoftwareSubscriptionsModal
                              open={taxSoftwareSubscriptionsModal.isOpen}
                              onClose={() =>
                                taxSoftwareSubscriptionsModal.close()
                              }
                              subscriptionPlans={taxSoftwareSubscriptions}
                              onProductSubscribe={onProductSubscribe}
                              isAddToCartFlow={isAddToCartFlow}
                            />
                          )}
                        </div>
                      ) : (
                        <Button
                          size="small"
                          onClick={() =>
                            onManageHandler(item, item.subscription_type)
                          }
                        >
                          Manage plan
                        </Button>
                      )
                    }
                    isSubscription
                  />
                )}
              </>
            );
          })}
        </Async.Success>
      </Async.Root>
    </section>
  );
};
